import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Switch,
  Space,
  Card,
  Typography,
  message,
  Popconfirm,
  Tag,
  Tooltip,
  Divider,
  Row,
  Col,
  Alert,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { supabase } from "../../lib/supabase";
import { trackEvent, captureError } from "../../lib/posthog";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Option } = Select;

interface PricingTemplate {
  id: number;
  name: string;
  transport_type: number | null;
  booking_type: number | null;
  incoterm: number | null;
  container_size: number | null;
  valid_from: string;
  valid_to: string | null;
  is_active: boolean;
  inherit_from_template_id: number | null;
  is_standalone: boolean;
  created_at: string;
  updated_at: string;
}

interface TemplateComponent {
  id: number;
  template_id: number;
  component_id: number;
  value: number | null;
  currency: string;
  is_active: boolean;
  included_quantity?: number;
  extra_price?: number;
  component_name?: string;
  component_description?: string;
  calculation_type?: string;
}

interface ComponentWithDeliveryType {
  id: number;
  name: string;
  description?: string;
  calculation_type?: string;
  applies_to_delivery_type?: "delivery" | "storage" | "both" | null;
  applies_to_shipment_type?: "carton" | "pallet" | "both" | null;
  [key: string]: any;
}

interface ReferenceData {
  transportTypes: any[];
  bookingTypes: any[];
  incoterms: any[];
  containerSizes: any[];
  components: ComponentWithDeliveryType[];
  originPorts: any[];
  destinationPorts: any[];
}

const PricingTemplatesTab: React.FC = () => {
  const [templates, setTemplates] = useState<PricingTemplate[]>([]);
  const [templateComponents, setTemplateComponents] = useState<
    TemplateComponent[]
  >([]);
  const [referenceData, setReferenceData] = useState<ReferenceData>({
    transportTypes: [],
    bookingTypes: [],
    incoterms: [],
    containerSizes: [],
    components: [],
    originPorts: [],
    destinationPorts: [],
  });
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [componentsModalVisible, setComponentsModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] =
    useState<PricingTemplate | null>(null);
  const [form] = Form.useForm();
  const transportType = Form.useWatch("transport_type", form);
  const bookingType = Form.useWatch("booking_type", form);
  const showContainerSize =
    transportType === 2 && bookingType === 2; // Seafreight + FCL
  const [filterTransportType, setFilterTransportType] = useState<number | null>(
    null,
  );
  const [filterIncoterm, setFilterIncoterm] = useState<number | null>(null);
  const [pageSize, setPageSize] = useState(25);

  // Fetch all data
  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch templates
      const { data: templatesData, error: templatesError } = await supabase
        .from("pricing_templates")
        .select("*")
        .order("name");

      if (templatesError) throw templatesError;

      // Fetch template components
      const { data: componentsData, error: componentsError } =
        await supabase.from("pricing_template_components").select(`
          *,
          pricing_components (
            name,
            description,
            calculation_type,
            applies_to_delivery_type,
            applies_to_shipment_type
          )
        `);

      if (componentsError) throw componentsError;

      // Fetch reference data
      const [
        { data: transportTypes },
        { data: bookingTypes },
        { data: incoterms },
        { data: containerSizes },
        { data: components },
        { data: originPorts },
        { data: destinationPorts },
      ] = await Promise.all([
        supabase.from("booking_transport_types").select("*").order("name"),
        supabase.from("bookings_type").select("*").order("name"),
        supabase.from("bookings_incoterm").select("*").order("name"),
        supabase.from("container_size").select("*").order("name"),
        supabase.from("pricing_components").select("*").order("name"),
        supabase.from("booking_origin_port").select("*").order("name"),
        supabase.from("booking_destination_port").select("*").order("name"),
      ]);

      setTemplates(templatesData || []);
      setTemplateComponents(componentsData || []);
      setReferenceData({
        transportTypes: transportTypes || [],
        bookingTypes: bookingTypes || [],
        incoterms: incoterms || [],
        containerSizes: containerSizes || [],
        components: components || [],
        originPorts: originPorts || [],
        destinationPorts: destinationPorts || [],
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      message.error("Failed to load pricing templates");
      captureError(error, {
        source: "PricingTemplatesTab",
        action: "fetchData",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditTemplate = (template: PricingTemplate) => {
    setEditingTemplate(template);
    form.setFieldsValue({
      ...template,
      valid_from: template.valid_from ? dayjs(template.valid_from) : null,
      valid_to: template.valid_to ? dayjs(template.valid_to) : null,
    });
    setModalVisible(true);
  };

  const handleSaveTemplate = async (values: any) => {
    try {
      // Auto-generate template name based on settings
      const generatedName = generateTemplateName(values, referenceData);

      const templateData = {
        ...values,
        name: generatedName, // Use auto-generated name
        valid_from:
          values.valid_from?.format("YYYY-MM-DD") ||
          new Date().toISOString().split("T")[0],
        valid_to: values.valid_to?.format("YYYY-MM-DD") || null,
      };

      if (editingTemplate) {
        const { error } = await supabase
          .from("pricing_templates")
          .update(templateData)
          .eq("id", editingTemplate.id);

        if (error) throw error;
        message.success("Template updated successfully");
      } else {
        const { error } = await supabase
          .from("pricing_templates")
          .insert(templateData);

        if (error) throw error;
        message.success("Template created successfully");
      }

      setModalVisible(false);
      fetchData();
      trackEvent("pricing_template_saved", {
        action: editingTemplate ? "update" : "create",
        template_name: generatedName,
      });
    } catch (error) {
      console.error("Error saving template:", error);
      message.error("Failed to save template");
      captureError(error, {
        source: "PricingTemplatesTab",
        action: "saveTemplate",
      });
    }
  };

  // Auto-generate template name based on settings
  const generateTemplateName = (values: any, refData: ReferenceData) => {
    const parts: string[] = [];

    // Transport type
    if (values.transport_type) {
      const transportType = refData.transportTypes.find(
        (t) => t.id === values.transport_type,
      );
      parts.push(transportType?.name || "Unknown Transport");
    }

    // Booking type (only for Seafreight)
    if (values.transport_type === 2 && values.booking_type) {
      const bookingType = refData.bookingTypes.find(
        (t) => t.id === values.booking_type,
      );
      parts.push(bookingType?.name || "Unknown Type");
    }

    // Incoterm
    if (values.incoterm) {
      const incoterm = refData.incoterms.find((i) => i.id === values.incoterm);
      parts.push(incoterm?.name || "Unknown Incoterm");
    }

    // Container size (only for FCL)
    if (values.booking_type === 2 && values.container_size) {
      const containerSize = refData.containerSizes.find(
        (s) => s.id === values.container_size,
      );
      parts.push(containerSize?.name || "Unknown Size");
    }

    // Origin port
    if (values.origin_port) {
      const originPort = refData.originPorts.find(
        (p) => p.id === values.origin_port,
      );
      parts.push(`From ${originPort?.name || "Unknown Port"}`);
    }

    // Destination port
    if (values.destination_port) {
      const destPort = refData.destinationPorts.find(
        (p) => p.id === values.destination_port,
      );
      parts.push(`To ${destPort?.name || "Unknown Port"}`);
    }

    // If no specific settings, it's a base template
    if (parts.length === 0) {
      return "Base Template";
    }

    return parts.join(" ");
  };

  const handleDeleteTemplate = async (templateId: number) => {
    try {
      // First check if this template is inherited by other templates
      const { data: childTemplates, error: checkError } = await supabase
        .from("pricing_templates")
        .select("id, name")
        .eq("inherit_from_template_id", templateId);

      if (checkError) throw checkError;

      if (childTemplates && childTemplates.length > 0) {
        const childNames = childTemplates.map((t) => t.name).join(", ");
        message.error(
          `Cannot delete this template because it is inherited by: ${childNames}. Please delete or update the child templates first.`,
        );
        return;
      }

      const { error } = await supabase
        .from("pricing_templates")
        .delete()
        .eq("id", templateId);

      if (error) throw error;
      message.success("Template deleted successfully");
      fetchData();
      trackEvent("pricing_template_deleted", { template_id: templateId });
    } catch (error) {
      console.error("Error deleting template:", error);
      message.error("Failed to delete template");
      captureError(error, {
        source: "PricingTemplatesTab",
        action: "deleteTemplate",
      });
    }
  };

  const handleManageComponents = (template: PricingTemplate) => {
    setEditingTemplate(template);
    setComponentsModalVisible(true);
  };

  const getTemplateComponents = (templateId: number) => {
    return templateComponents.filter((tc) => tc.template_id === templateId);
  };

  const getReferenceName = (id: number | null, type: keyof ReferenceData) => {
    if (!id) return "-";
    const item = referenceData[type].find((item: any) => item.id === id);
    return item?.name || "Unknown";
  };

  const columns = [
    {
      title: "Template Name",
      dataIndex: "name",
      key: "name",
      render: (name: string, record: PricingTemplate) => {
        const hasChildren = templates.some(
          (t) => t.inherit_from_template_id === record.id,
        );

        return (
          <div>
            <Text strong>{name}</Text>
            {record.is_standalone && (
              <Tag color="blue" style={{ marginLeft: 8 }}>
                Standalone
              </Tag>
            )}
            {record.inherit_from_template_id && (
              <Tag color="green" style={{ marginLeft: 8 }}>
                Inherits
              </Tag>
            )}
            {hasChildren && (
              <Tag color="orange" style={{ marginLeft: 8 }}>
                Has Children
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: "Transport Type",
      dataIndex: "transport_type",
      key: "transport_type",
      render: (transportType: number) =>
        getReferenceName(transportType, "transportTypes"),
    },
    {
      title: "Booking Type",
      dataIndex: "booking_type",
      key: "booking_type",
      render: (bookingType: number) =>
        getReferenceName(bookingType, "bookingTypes"),
    },
    {
      title: "Incoterm",
      dataIndex: "incoterm",
      key: "incoterm",
      render: (incoterm: number) => getReferenceName(incoterm, "incoterms"),
    },
    {
      title: "Origin Port",
      dataIndex: "origin_port",
      key: "origin_port",
      render: (originPort: number) =>
        getReferenceName(originPort, "originPorts"),
    },
    {
      title: "Destination Port",
      dataIndex: "destination_port",
      key: "destination_port",
      render: (destinationPort: number) =>
        getReferenceName(destinationPort, "destinationPorts"),
    },
    {
      title: "Container Size",
      dataIndex: "container_size",
      key: "container_size",
      render: (containerSize: number) =>
        getReferenceName(containerSize, "containerSizes"),
    },
    {
      title: "Valid From",
      dataIndex: "valid_from",
      key: "valid_from",
      render: (date: string) => dayjs(date).format("YYYY-MM-DD"),
    },
    {
      title: "Valid To",
      dataIndex: "valid_to",
      key: "valid_to",
      render: (date: string | null) =>
        date ? dayjs(date).format("YYYY-MM-DD") : "∞",
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      render: (isActive: boolean) => (
        <Tag color={isActive ? "green" : "red"}>
          {isActive ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Components",
      key: "components",
      render: (record: PricingTemplate) => {
        const components = getTemplateComponents(record.id);
        const activeComponents = components.filter((tc) => tc.is_active);
        return (
          <Text type="secondary">
            {activeComponents.length} / {components.length} active
          </Text>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (record: PricingTemplate) => (
        <Space>
          <Tooltip title="Manage Components">
            <Button
              type="text"
              icon={<SettingOutlined />}
              onClick={() => handleManageComponents(record)}
            />
          </Tooltip>
          <Tooltip title="Edit Template">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditTemplate(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this template?"
            description="This action cannot be undone."
            onConfirm={() => handleDeleteTemplate(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Filter templates based on selected filters
  const filteredTemplates = templates.filter((template) => {
    if (
      filterTransportType &&
      template.transport_type !== filterTransportType
    ) {
      return false;
    }
    if (filterIncoterm && template.incoterm !== filterIncoterm) {
      return false;
    }
    return true;
  });

  return (
    <div>
      <Card>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <Title level={4}>Pricing Templates</Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreateTemplate}
          >
            Create Template
          </Button>
        </div>

        {/* Filters */}
        <div
          style={{
            marginBottom: 16,
            display: "flex",
            gap: 16,
            alignItems: "center",
          }}
        >
          <Space>
            <span>Transport Type:</span>
            <Select
              placeholder="All Transport Types"
              allowClear
              style={{ width: 200 }}
              value={filterTransportType}
              onChange={(value) => setFilterTransportType(value || null)}
              options={referenceData.transportTypes.map((type) => ({
                label: type.name,
                value: type.id,
              }))}
            />
          </Space>
          <Space>
            <span>Incoterm:</span>
            <Select
              placeholder="All Incoterms"
              allowClear
              style={{ width: 200 }}
              value={filterIncoterm}
              onChange={(value) => setFilterIncoterm(value || null)}
              options={referenceData.incoterms.map((incoterm) => ({
                label: incoterm.name,
                value: incoterm.id,
              }))}
            />
          </Space>
          {(filterTransportType !== null || filterIncoterm !== null) && (
            <Button
              size="small"
              onClick={() => {
                setFilterTransportType(null);
                setFilterIncoterm(null);
              }}
            >
              Clear Filters
            </Button>
          )}
        </div>

        <Table
          columns={columns}
          dataSource={filteredTemplates}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize,
            showSizeChanger: true,
            pageSizeOptions: ["10", "25", "50", "100"],
            onShowSizeChange: (_, size) => setPageSize(size),
            showTotal: (total) => `${total} templates`,
          }}
        />
      </Card>

      {/* Template Form Modal */}
      <Modal
        title={
          editingTemplate
            ? "Edit Template"
            : "Create Template (Name Auto-Generated)"
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSaveTemplate}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="is_active"
                label="Active"
                valuePropName="checked"
                initialValue={true}
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="transport_type"
                label="Transport Type"
                rules={[
                  { required: true, message: "Please select transport type" },
                ]}
              >
                <Select placeholder="Select transport type">
                  {referenceData.transportTypes.map((type) => (
                    <Option key={type.id} value={type.id}>
                      {type.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item noStyle dependencies={["transport_type"]}>
                {({ getFieldValue }) => {
                  const transportType = getFieldValue("transport_type");
                  const isSeafreight = transportType === 2; // Seafreight

                  if (!isSeafreight) {
                    return null; // Don't render for Airfreight or when not selected
                  }

                  return (
                    <Form.Item name="booking_type" label="Booking Type">
                      <Select placeholder="Select booking type" allowClear>
                        {referenceData.bookingTypes.map((type) => (
                          <Option key={type.id} value={type.id}>
                            {type.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="incoterm" label="Incoterm">
                <Select placeholder="Select incoterm" allowClear>
                  {referenceData.incoterms.map((incoterm) => (
                    <Option key={incoterm.id} value={incoterm.id}>
                      {incoterm.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="origin_port" label="Origin Port">
                <Select placeholder="Select origin port" allowClear>
                  {referenceData.originPorts.map((port) => (
                    <Option key={port.id} value={port.id}>
                      {port.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="destination_port" label="Destination Port">
                <Select placeholder="Select destination port" allowClear>
                  {referenceData.destinationPorts.map((port) => (
                    <Option key={port.id} value={port.id}>
                      {port.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="valid_from"
                label="Valid From"
                initialValue={dayjs()}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              {showContainerSize ? (
                <Form.Item name="container_size" label="Container Size">
                  <Select
                    placeholder="Select container size"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    options={referenceData.containerSizes.map((size) => ({
                      label: size.name,
                      value: size.id,
                    }))}
                  />
                </Form.Item>
              ) : null}
            </Col>
            <Col span={8}>
              <Form.Item name="valid_to" label="Valid To">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>{/* Empty column for spacing */}</Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="is_standalone"
                label="Standalone Template"
                valuePropName="checked"
                initialValue={false}
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="inherit_from_template_id" label="Inherit From">
                <Select placeholder="Select base template" allowClear>
                  {templates
                    .filter((t) => t.is_standalone)
                    .map((template) => (
                      <Option key={template.id} value={template.id}>
                        {template.name}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingTemplate ? "Update" : "Create"}
              </Button>
              <Button onClick={() => setModalVisible(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Components Management Modal */}
      <Modal
        title={`Manage Components - ${editingTemplate?.name}`}
        open={componentsModalVisible}
        onCancel={() => setComponentsModalVisible(false)}
        footer={null}
        width={1000}
        style={{ top: 20 }}
        bodyStyle={{ height: "75vh", padding: "16px", overflow: "hidden" }}
      >
        <TemplateComponentsManager
          template={editingTemplate}
          components={referenceData.components}
          templateComponents={getTemplateComponents(editingTemplate?.id || 0)}
          allTemplates={templates}
          allTemplateComponents={templateComponents}
          onSave={() => {
            setComponentsModalVisible(false);
            fetchData();
          }}
        />
      </Modal>
    </div>
  );
};

// Template Components Manager Component
interface TemplateComponentsManagerProps {
  template: PricingTemplate | null;
  components: ComponentWithDeliveryType[];
  templateComponents: TemplateComponent[];
  allTemplates: PricingTemplate[];
  allTemplateComponents: TemplateComponent[];
  onSave: () => void;
}

const TemplateComponentsManager: React.FC<TemplateComponentsManagerProps> = ({
  template,
  components,
  templateComponents,
  allTemplates,
  allTemplateComponents,
  onSave,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [showExcludedComponents, setShowExcludedComponents] = useState(false);

  // Components that should NOT be added to pricing templates
  // These are automatically calculated or come from other sources.
  // Export Clearance / Export Clearance License are NOT excluded — they can be set per template.
  const excludedComponentNames = [
    "Pickup Fee", // Optional/additional cost, comes from booking field
    "Transport Cost", // Automatically calculated from tariffs
    "Diesel Surcharge", // Automatically calculated (7% of transport for airfreight)
    "LMD", // Automatically calculated from LMD settings/rates
    "LMD Diesel Surcharge", // Automatically calculated from LMD settings
    "Tol surcharge", // Automatically calculated from LMD settings
  ];

  // Get inherited components from parent template
  const getInheritedComponents = () => {
    if (!template || !template.inherit_from_template_id) return {};

    const parentTemplate = allTemplates.find(
      (t) => t.id === template.inherit_from_template_id,
    );
    if (!parentTemplate) return {};

    const inheritedValues: any = {};

    // Get parent template components
    const parentComponents = allTemplateComponents.filter(
      (tc) => tc.template_id === parentTemplate.id,
    );

    parentComponents.forEach((parentTc) => {
      if (parentTc.is_active && parentTc.value) {
        inheritedValues[parentTc.component_id] = {
          value: parentTc.value,
          currency: parentTc.currency,
          included_quantity: parentTc.included_quantity,
          extra_price: parentTc.extra_price,
          component_name: parentTc.component_name,
        };
      }
    });

    return inheritedValues;
  };

  useEffect(() => {
    if (template) {
      const initialValues: any = {};
      const inheritedValues = getInheritedComponents();

      // For each component, set up initial values
      components.forEach((component) => {
        // Find if this component exists in current template
        const existingTc = templateComponents.find(
          (tc) => tc.component_id === component.id,
        );

        if (existingTc) {
          // Component has override values
          initialValues[`component_${component.id}`] = existingTc.value;
          initialValues[`currency_${component.id}`] =
            existingTc.currency || "EUR";
          initialValues[`active_${component.id}`] = existingTc.is_active;
          initialValues[`included_${component.id}`] =
            existingTc.included_quantity || 1;
          initialValues[`extra_${component.id}`] = existingTc.extra_price || 0;
        } else {
          // Component doesn't exist, use defaults
          initialValues[`component_${component.id}`] = null;
          initialValues[`currency_${component.id}`] = "EUR";
          initialValues[`active_${component.id}`] = false; // Not overriding by default
          initialValues[`included_${component.id}`] = 1;
          initialValues[`extra_${component.id}`] = 0;
        }
      });

      form.setFieldsValue(initialValues);
    }
  }, [template, templateComponents, components, form]);

  const handleSaveComponents = async (values: any) => {
    if (!template) return;

    try {
      setLoading(true);

      // Only save components that are being overridden (active = true)
      // Also filter out excluded components (should never be saved to templates)
      const componentData = components
        .filter((component) => {
          // First check if component is excluded (should never be saved to templates)
          const name = component.name?.toLowerCase() || "";

          // Check against excluded component names
          const isExcluded = excludedComponentNames.some((excludedName) => {
            const excludedLower = excludedName.toLowerCase();
            return name === excludedLower || name.includes(excludedLower);
          });

          // Also check for common patterns (export+clearance excluded from patterns — allow per-template)
          const hasExcludedPattern =
            name.includes("pickup") ||
            (name.includes("transport") && name.includes("cost")) ||
            (name.includes("diesel") && name.includes("surcharge")) ||
            name === "lmd" ||
            (name.includes("lmd") &&
              (name.includes("diesel") || name.includes("surcharge")));

          // Exception: Allow "Administration fee" components even if they contain "LMD"
          if (name.includes("administration")) {
            // Allow administration fees, continue to check if active
          } else if (isExcluded || hasExcludedPattern) {
            return false; // Never save excluded components
          }

          const isActive = values[`active_${component.id}`] || false;
          return isActive; // Only include components that are being overridden
        })
        .map((component) => {
          const value = values[`component_${component.id}`];
          const currency = values[`currency_${component.id}`] || "EUR";
          const includedQuantity = values[`included_${component.id}`] || 1;
          const extraPrice = values[`extra_${component.id}`] || 0;

          return {
            template_id: template.id,
            component_id: component.id,
            value: value || null,
            currency,
            is_active: true, // Always true for overridden components
            included_quantity: includedQuantity,
            extra_price: extraPrice,
          };
        });

      // Delete existing components for this template
      await supabase
        .from("pricing_template_components")
        .delete()
        .eq("template_id", template.id);

      // Insert only the overridden components
      if (componentData.length > 0) {
        const { error } = await supabase
          .from("pricing_template_components")
          .insert(componentData);

        if (error) throw error;
      }

      message.success("Components updated successfully");
      onSave();
    } catch (error) {
      console.error("Error saving components:", error);
      message.error("Failed to save components");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSaveComponents}
        style={{ height: "100%" }}
      >
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            paddingRight: "8px",
            maxHeight: "calc(75vh - 120px)", // Account for modal header and footer
            paddingBottom: "8px", // Add space at bottom for scroll
            border: "1px solid #f0f0f0",
            borderRadius: "6px",
            padding: "8px",
          }}
        >
          <Alert
            type="info"
            showIcon
            message={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>Automatic Components</span>
                <Switch
                  size="small"
                  checked={showExcludedComponents}
                  onChange={setShowExcludedComponents}
                  checkedChildren="Show"
                  unCheckedChildren="Hide"
                />
              </div>
            }
            description="The following components are automatically calculated and should NOT be added to templates: Transport Cost (from tariffs), Diesel Surcharge (7% of transport for airfreight), LMD (from LMD settings), Pickup Fee (optional, from booking). Export Clearance and Export Clearance License can be set per template. Use the toggle to temporarily show excluded components if they were accidentally enabled."
            style={{ marginBottom: 16 }}
          />
          {components
            .filter((component) => {
              // Filter out components that should NOT be in pricing templates
              const name = component.name?.toLowerCase() || "";

              // Check against excluded component names (exact match or contains)
              const shouldExclude = excludedComponentNames.some(
                (excludedName) => {
                  const excludedLower = excludedName.toLowerCase();
                  return name === excludedLower || name.includes(excludedLower);
                },
              );

              // Also check for common variations and patterns (export+clearance allowed — not excluded)
              const hasExcludedPattern =
                name.includes("pickup") ||
                (name.includes("transport") && name.includes("cost")) ||
                (name.includes("diesel") && name.includes("surcharge")) ||
                name === "lmd" ||
                (name.includes("lmd") &&
                  (name.includes("diesel") || name.includes("surcharge")));

              // Exception: Allow "Administration fee" components even if they contain "LMD"
              if (name.includes("administration")) {
                return true; // Always allow administration fees
              }

              // If toggle is enabled, show excluded components; otherwise filter them out
              if (showExcludedComponents) {
                return true; // Show all components when toggle is enabled
              }

              return !shouldExclude && !hasExcludedPattern;
            })
            .map((component) => {
              const inheritedValues = getInheritedComponents();
              const hasInheritance = inheritedValues[component.id];
              const isOverriding = form.getFieldValue(`active_${component.id}`);

              // Check if this is an excluded/automatic component
              const componentName = component.name?.toLowerCase() || "";
              const isExcludedComponent =
                excludedComponentNames.some((excludedName) => {
                  const excludedLower = excludedName.toLowerCase();
                  return (
                    componentName === excludedLower ||
                    componentName.includes(excludedLower)
                  );
                }) ||
                componentName.includes("pickup") ||
                (componentName.includes("transport") &&
                  componentName.includes("cost")) ||
                (componentName.includes("diesel") &&
                  componentName.includes("surcharge")) ||
                componentName === "lmd" ||
                (componentName.includes("lmd") &&
                  (componentName.includes("diesel") ||
                    componentName.includes("surcharge")));

              return (
                <Card
                  key={component.id}
                  size="small"
                  style={{ marginBottom: 6 }}
                >
                  {component.calculation_type === "per_unique_product" ? (
                    // Special layout for tiered pricing components
                    <>
                      <Row gutter={12} align="middle">
                        <Col span={8}>
                          <div style={{ lineHeight: "1.3" }}>
                            <Space>
                              <Text strong style={{ fontSize: "14px" }}>
                                {component.name}
                              </Text>
                              {isExcludedComponent && (
                                <Tag color="red" style={{ fontSize: "10px" }}>
                                  Automatic - Disable
                                </Tag>
                              )}
                            </Space>
                            <br />
                            <Text type="secondary" style={{ fontSize: "12px" }}>
                              {component.description}
                            </Text>
                            <br />
                            <Space size={4} style={{ marginTop: "2px" }}>
                              <Tag color="blue" style={{ fontSize: "11px" }}>
                                {component.calculation_type}
                              </Tag>
                              {(() => {
                                // Component data comes from referenceData.components which has applies_to_delivery_type
                                // Access the field directly from the component object
                                const deliveryType = (
                                  component as ComponentWithDeliveryType
                                ).applies_to_delivery_type;
                                // Show tag based on deliveryType
                                if (deliveryType === "delivery") {
                                  return (
                                    <Tag
                                      color="cyan"
                                      style={{ fontSize: "11px" }}
                                    >
                                      🚚 Delivery
                                    </Tag>
                                  );
                                }
                                if (deliveryType === "storage") {
                                  return (
                                    <Tag
                                      color="orange"
                                      style={{ fontSize: "11px" }}
                                    >
                                      📦 Storage
                                    </Tag>
                                  );
                                }
                                // If null/undefined or "both", show "Both" tag
                                return (
                                  <Tag
                                    color="default"
                                    style={{ fontSize: "11px" }}
                                  >
                                    📦🚚 Both
                                  </Tag>
                                );
                              })()}
                            </Space>
                            {hasInheritance && (
                              <>
                                <br />
                                <Text
                                  type="secondary"
                                  style={{ fontSize: "10px" }}
                                >
                                  Inherited: €{hasInheritance.value}
                                  {hasInheritance.included_quantity &&
                                    hasInheritance.extra_price &&
                                    ` (${hasInheritance.included_quantity} incl, €${hasInheritance.extra_price} extra)`}
                                </Text>
                              </>
                            )}
                          </div>
                        </Col>
                        <Col span={4}>
                          <Form.Item
                            name={`component_${component.id}`}
                            label="Base Price"
                            style={{ marginBottom: 0 }}
                          >
                            <Input
                              type="number"
                              placeholder="0.00"
                              step="0.01"
                              min="0"
                              size="small"
                            />
                          </Form.Item>
                        </Col>
                        <Col span={3}>
                          <Form.Item
                            name={`included_${component.id}`}
                            label="Included"
                            style={{ marginBottom: 0 }}
                          >
                            <Input
                              type="number"
                              placeholder="2"
                              step="1"
                              min="1"
                              size="small"
                            />
                          </Form.Item>
                        </Col>
                        <Col span={4}>
                          <Form.Item
                            name={`extra_${component.id}`}
                            label="Extra Price"
                            style={{ marginBottom: 0 }}
                          >
                            <Input
                              type="number"
                              placeholder="0.00"
                              step="0.01"
                              min="0"
                              size="small"
                            />
                          </Form.Item>
                        </Col>
                        <Col span={3}>
                          <Form.Item
                            name={`currency_${component.id}`}
                            label="Currency"
                            initialValue="EUR"
                            style={{ marginBottom: 0 }}
                          >
                            <Select size="small">
                              <Option value="EUR">EUR</Option>
                              <Option value="USD">USD</Option>
                              <Option value="CNY">CNY</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={2}>
                          <Form.Item
                            name={`active_${component.id}`}
                            label={hasInheritance ? "Override" : "Active"}
                            valuePropName="checked"
                            initialValue={false}
                            style={{ marginBottom: 0 }}
                          >
                            <Switch size="small" />
                          </Form.Item>
                        </Col>
                      </Row>
                    </>
                  ) : (
                    // Standard layout for regular components
                    <Row gutter={12} align="middle">
                      <Col span={10}>
                        <div style={{ lineHeight: "1.3" }}>
                          <Space>
                            <Text strong style={{ fontSize: "14px" }}>
                              {component.name}
                            </Text>
                            {isExcludedComponent && (
                              <Tag color="red" style={{ fontSize: "10px" }}>
                                Automatic - Disable
                              </Tag>
                            )}
                          </Space>
                          <br />
                          <Text type="secondary" style={{ fontSize: "12px" }}>
                            {component.description}
                          </Text>
                          <br />
                          <Space size={4} style={{ marginTop: "2px" }}>
                            <Tag color="blue" style={{ fontSize: "11px" }}>
                              {component.calculation_type}
                            </Tag>
                            {(() => {
                              // Component data comes from referenceData.components which has applies_to_delivery_type
                              // Access the field directly from the component object
                              const deliveryType = (
                                component as ComponentWithDeliveryType
                              ).applies_to_delivery_type;
                              // Show tag based on deliveryType
                              if (deliveryType === "delivery") {
                                return (
                                  <Tag
                                    color="cyan"
                                    style={{ fontSize: "11px" }}
                                  >
                                    🚚 Delivery
                                  </Tag>
                                );
                              }
                              if (deliveryType === "storage") {
                                return (
                                  <Tag
                                    color="orange"
                                    style={{ fontSize: "11px" }}
                                  >
                                    📦 Storage
                                  </Tag>
                                );
                              }
                              // If null/undefined or "both", show "Both" tag
                              return (
                                <Tag
                                  color="default"
                                  style={{ fontSize: "11px" }}
                                >
                                  📦🚚 Both
                                </Tag>
                              );
                            })()}
                          </Space>
                          {hasInheritance && (
                            <>
                              <br />
                              <Text
                                type="secondary"
                                style={{ fontSize: "10px" }}
                              >
                                Inherited: €{hasInheritance.value}
                              </Text>
                            </>
                          )}
                        </div>
                      </Col>
                      <Col span={5}>
                        <Form.Item
                          name={`component_${component.id}`}
                          label="Value"
                          style={{ marginBottom: 0 }}
                        >
                          <Input
                            type="number"
                            placeholder="0.00"
                            step="0.01"
                            min="0"
                            size="small"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={5}>
                        <Form.Item
                          name={`currency_${component.id}`}
                          label="Currency"
                          initialValue="EUR"
                          style={{ marginBottom: 0 }}
                        >
                          <Select size="small">
                            <Option value="EUR">EUR</Option>
                            <Option value="USD">USD</Option>
                            <Option value="CNY">CNY</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          name={`active_${component.id}`}
                          label={hasInheritance ? "Override" : "Active"}
                          valuePropName="checked"
                          initialValue={false}
                          style={{ marginBottom: 0 }}
                        >
                          <Switch size="small" />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}
                </Card>
              );
            })}
        </div>

        <div
          style={{
            padding: "12px 0",
            borderTop: "1px solid #f0f0f0",
            marginTop: "8px",
          }}
        >
          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                Save Components
              </Button>
              <Button onClick={onSave}>Cancel</Button>
            </Space>
          </Form.Item>
        </div>
      </Form>
    </div>
  );
};

export default PricingTemplatesTab;
